/**
 * Research browse + search data hook.
 *
 * Powers the Research page's grouped grid. Fires on both empty and non-empty
 * queries:
 *
 * - **Empty query → browse mode.** Backend returns per-type previews ordered
 *   by mention count. Cached server-side, so it fires at once.
 * - **Non-empty query → search mode.** Backend runs entity + chunk + doc
 *   fuzzy search and groups the entity hits by type. Debounced by
 *   `DEBOUNCE_MS` so keystrokes don't fire a request per character.
 *
 * A `documentId` narrows either mode to what that document yielded. The previous
 * answer stays on screen while the next one loads; status is `loading` from the
 * moment the filter changes.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useDebounced } from '@/hooks/use-debounced'
import { type SearchContentResponse, searchCaseContent } from '@/services/api/case-search'

export type BrowseStatus = 'loading' | 'done' | 'error'

export interface UseBrowseExtractedResult {
  results: SearchContentResponse | null
  status: BrowseStatus
}

const DEBOUNCE_MS = 280

export function useBrowseExtracted(
  caseId: string,
  query: string,
  documentId?: string
): UseBrowseExtractedResult {
  const trimmed = query.trim()
  const debounced = useDebounced(trimmed, DEBOUNCE_MS)
  const term = trimmed && debounced
  const search = useQuery({
    queryKey: ['case-research-browse', caseId, term, documentId] as const,
    queryFn: () => searchCaseContent(caseId, term, { documentId }),
    placeholderData: keepPreviousData,
    // The server caches this per extraction: coming back to the page asks it again.
    staleTime: 0,
  })
  const status: BrowseStatus = search.isError
    ? 'error'
    : search.isPending || search.isPlaceholderData || term !== trimmed
      ? 'loading'
      : 'done'
  return { results: search.data ?? null, status }
}
